'use client';
 

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import { useShallow } from 'zustand/react/shallow';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { DEFAULT_FILTERS, FORM_TYPES, Filters, UISubmissionStatus, UISubmissionStatusLabels } from '@ifa/types';
import { getTransplantCenterIds, useIfaStore } from '@ifa/store';
import debounce from 'debounce';

import { useRidMutation } from '../hooks/useRidMutation';

import DashboardFiltersBtnClear from './DashboardFiltersBtnClear';

export type DashboardFiltersProps = {
    filters: Filters;
    setFilters: (filters: Filters) => void;
    page: number;
    setPage: (page: number) => void;
};

function DashboardFilters({ filters, setFilters, page, setPage }: DashboardFiltersProps) {
    const tcIds = useIfaStore(useShallow(state => getTransplantCenterIds(state)));

    const methods = useForm({
        defaultValues: filters,
        mode: 'onBlur',
    });

    const clearFilters = () => {
        setPage(0);
        methods.reset();
        setFilters({
            ...DEFAULT_FILTERS,
        });
    };

    const onSubmit = (data: Filters) => {
        if (page > 0) {
            setPage(0);
        }
        setFilters({
            ...data,
        });
    };

    const { mutateUiRid } = useRidMutation();

    return (
        <Box marginTop={2} marginBottom={3} data-cy="filters">
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)} onBlur={methods.handleSubmit(onSubmit)} role="search">
                    <Grid container spacing={2} columns={10}>
                        <Grid item xs={10} sm={2}>
                            <Controller
                                name="tcId"
                                control={methods.control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            {...field}
                                            id="tcId"
                                            value={field.value}
                                            options={tcIds}
                                            noOptionsText="No matches"
                                            onChange={(event, newValue) => {
                                                field.onChange(newValue);
                                                methods.handleSubmit(onSubmit)();
                                            }}
                                            disablePortal
                                            data-cy="filters-tcId"
                                            getOptionLabel={option => option.toString()}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="TC ID"
                                                    onKeyDown={ev => {
                                                        const numericKeys = [
                                                            'ArrowUp',
                                                            'ArrowDown',
                                                            'ArrowLeft',
                                                            'ArrowRight',
                                                            'Backspace',
                                                            'Enter',
                                                            'Tab',
                                                            'Digit0',
                                                            'Digit1',
                                                            'Digit2',
                                                            'Digit3',
                                                            'Digit4',
                                                            'Digit5',
                                                            'Digit6',
                                                            'Digit7',
                                                            'Digit8',
                                                            'Digit9',
                                                            'Numpad0',
                                                            'Numpad1',
                                                            'Numpad2',
                                                            'Numpad3',
                                                            'Numpad4',
                                                            'Numpad5',
                                                            'Numpad6',
                                                            'Numpad7',
                                                            'Numpad8',
                                                            'Numpad9',
                                                        ];
                                                        if (!numericKeys.includes(ev.code)) {
                                                            ev.preventDefault();
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                            <Controller
                                name="rid"
                                control={methods.control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <TextField
                                            {...field}
                                            id="rid"
                                            label="Patient ID"
                                            data-cy="filters-rid"
                                            onChange={e => {
                                                field.onChange(mutateUiRid(e.target.value));
                                                // Call submit on rid change every 500ms to update filters without apply/submit
                                                methods.handleSubmit(debounce(onSubmit, 500))();
                                            }}
                                        />
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                            <Controller
                                name="formType"
                                control={methods.control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel id="formType-label">Form name</InputLabel>
                                        <Select
                                            {...field}
                                            id="formType"
                                            labelId="formType-label"
                                            data-cy="filters-formType"
                                            label="Form name"
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            {/* TODO remove or make use of formType */}
                                            {Object.keys(FORM_TYPES).map((formType, index) => (
                                                <MenuItem key={index} value={FORM_TYPES[index]}>
                                                    {FORM_TYPES[index]}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                            <Controller
                                name="status"
                                control={methods.control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel id="status-label">Status</InputLabel>
                                        <Select
                                            {...field}
                                            id="status"
                                            labelId="status-label"
                                            data-cy="filters-status"
                                            label="Status"
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            {Object.keys(UISubmissionStatusLabels)
                                                .filter(status => status !== 'DELETED') // DELETED status is not displayed in the filter
                                                .map(status => (
                                                    <MenuItem key={status} value={status}>
                                                        {UISubmissionStatusLabels[status as UISubmissionStatus]}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid item xs={10} sm={2}>
                            <DashboardFiltersBtnClear clearFilters={clearFilters} filters={filters} />
                        </Grid>
                    </Grid>
                </form>
            </FormProvider>
        </Box>
    );
}

export default DashboardFilters;
